import React, {useEffect, useState} from 'react'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Helmet } from 'react-helmet'
import {Heading, HydraTable, NotFoundMessage, UnauthorisedMessage} from "@peracto/peracto-ui";
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage'

import { GET_LIST, GET_ONE, useClient } from '@peracto/client'
import LoadingIcon from "@peracto/peracto-ui/dist/LoadingIcon";

const ProductImage = ({ location, identifier }) => {
    return location ? (
        <img
            src={location}
            style={{ height: '40px', width: '40px' }}
            alt={identifier}
        />
    ) : (
        <FontAwesomeIcon
            icon={faImage}
            style={{ width: '40px', height: '40px', color: '#606060' }}
        />
    )
}

const ProductOptionDetailsListContainer = ({ children }) => {
    const { id } = useParams();
    const [ productOption, setProductOption ] = useState();
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const pathname = `/option/${id}/details`;

    const {client} = useClient();

    const fetchProductOption = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'product-options', {
                id: `/product-options/${id}`,
            })

            setProductOption(data);

            if (response.status === 404) {
                setNotFound(true)
            }
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }
        }
    }

    useEffect(() => {
        fetchProductOption();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url={`/products`}
                message="The product option you're looking for could not be found"
                buttonLabel="Go to Products"
            />
        )
    }

    if (!productOption) {
        return (
            <div style={{ height: '100vh' }}>
                <div className="d-flex flex-grow-1 align-items-center flex-column justify-content-center h-100">
                    <LoadingIcon />
                </div>
            </div>
        )
    }

    const productId = productOption.product.split('/').pop();

    return (
        <>
            <Helmet>
                <title>Product Option Details | Peracto</title>
            </Helmet>
            <Heading
                name={`Product Option Details for ${productOption.name}`}
            >
                <div className="d-flex align-items-center ml-2">
                    <Link
                        className="btn btn-outline-primary mr-2"
                        to={`/products/${productId}`}
                        data-testid="back-to-product"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
                        Back to Product
                    </Link>

                    <Link
                        className="btn btn-primary"
                        to={`/options/${id}/details/add`}
                        data-testid="add-product-variant"
                    >
                        <FontAwesomeIcon icon={faPlusCircle} className="mr-2" />
                        Add Product Option Detail
                    </Link>
                </div>
            </Heading>
            {children}
        </>
    )
}

const ProductOptionDetailsList = () => {
    const { id } = useParams();
    const { client } = useClient();

    const [selectedColumns, onSelectColumn] = useState({
        variantOptionIdentifier: true
    })

    const columns = [
        {
            Header: 'Image',
            accessor: 'location',
            selectable: false,
            Cell: row => {
                const location = row.row.values.location;

                const detailId = row.row.original.originId;
                return (
                    <Link
                        to={`/options/${id}/details/${detailId}`}
                        data-testid={`row-${row.row.index}-image`}
                    >
                        <ProductImage location={location} />
                    </Link>
                )
            },
        },
        {
            Header: 'Variant Option Identifier',
            accessor: 'variantOptionIdentifier',
            selectable: false,
            Cell: row => {
                const identifier = row.row.values.variantOptionIdentifier;

                const detailId = row.row.original.originId;
                return (
                    <Link
                        to={`/options/${id}/details/${detailId}`}
                        data-testid={`row-${row.row.index}-identifier`}
                    >
                        {identifier}
                    </Link>
                )
            },
        },
        {
            Header: 'Description',
            accessor: 'description',
            selectable: false,
            Cell: row => {
                const description = row.row.values.description;
                const maxCharacterLength = 75;
                const shortDescription = !description
                    ? "-"
                    : description.length <= maxCharacterLength
                        ? description
                        : description.slice(0, description.lastIndexOf(' ', maxCharacterLength)) + '...';

                return (
                    <p data-testid={`row-${row.row.index}-description`}>{shortDescription}</p>
                )
            },
        },
        {
            Header: 'Express Delivery',
            accessor: 'expressDelivery',
            selectable: false,
            Cell: row => {
                const expressDelivery = row.row.values.expressDelivery;

                return (
                    <p data-testid={`row-${row.row.index}-express-delivery`}>{expressDelivery ? 'Yes' : 'No'}</p>
                )
            },
        },
        {
            Header: 'Best Seller',
            accessor: 'bestSeller',
            selectable: false,
            Cell: row => {
                const bestSeller = row.row.values.bestSeller;

                return (
                    <p data-testid={`row-${row.row.index}-best-seller`}>{bestSeller ? 'Yes' : 'No'}</p>
                )
            },
        },
    ];

    const onFetchData = options => client(GET_LIST, 'product-option-details', options)

    return (
        <>
            <ProductOptionDetailsListContainer>
                <div className="row">
                    <div className="col">
                        <div className="card card--transparent">
                            <div className="card-body">
                                <HydraTable
                                    columns={columns}
                                    selectedColumns={selectedColumns}
                                    onFetchData={onFetchData}
                                    onSelectColumn={onSelectColumn}
                                    alwaysFilter={{
                                        productOption: id,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ProductOptionDetailsListContainer>
        </>
    )
}

export default ProductOptionDetailsList;